import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';

// Formatting helpers
function formatBytes(bytes) {
  if (bytes == null) return 'N/A';
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${sizes[i]}`;
}

function formatCPU(cpu) {
  if (cpu == null) return 'N/A';
  return `${cpu.toFixed(1)}%`;
}

function formatUptime(ms) {
  if (!ms || ms <= 0) return 'N/A';
  let seconds = Math.floor(ms / 1000);
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  return [
    days ? `${days}d` : null,
    hours ? `${hours}h` : null,
    minutes ? `${minutes}m` : null,
    `${seconds}s`,
  ].filter(Boolean).join(' ');
}

// CSS Spinner
const spinnerStyle = {
  width: '48px',
  height: '48px',
  border: '5px solid #ccc',
  borderTopColor: '#1d4ed8',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '1rem',
};

const SpinnerCSS = () => (
  <style>{`
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}</style>
);

// Modal for QR Code
function AuthModal({ visible, qrCodeData, authError, authBotName, onClose }) {
  if (!visible) return null;

  return (
    <>
      <SpinnerCSS />
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        <div className="bg-white rounded p-6 max-w-sm w-full relative shadow-lg">
          <button
            onClick={qrCodeData ? onClose : undefined}
            className={`absolute top-2 right-2 text-gray-700 hover:text-black focus:outline-none ${!qrCodeData ? 'cursor-not-allowed opacity-50' : ''}`}
            aria-label="Close modal"
            disabled={!qrCodeData}
          >
            ✕
          </button>
          <h2 id="auth-modal-title" className="text-xl font-bold mb-4">
            Authorize Bot {authBotName}
          </h2>

          {authError && <p className="text-red-600 text-center mb-2">{authError}</p>}
          {qrCodeData ? (
            <QRCodeCanvas value={qrCodeData} size={256} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={spinnerStyle} />
              <p>Waiting for QR code…</p>
            </div>
          )}
          <p className="mt-4 text-center">
            Scan this QR code with your WhatsApp app to authorize the bot.
          </p>
        </div>
      </div>
    </>
  );
}

export default function BotList() {
  const [bots, setBots] = useState([]);
  const [memLimits, setMemLimits] = useState({});
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [authError, setAuthError] = useState(null);
  const intervalRef = useRef(null);
  const [authBotName, setAuthBotName] = useState(null);
  const authBotNameRef = useRef(authBotName);
  const [authCooldowns, setAuthCooldowns] = useState({});
  const wsRef = useRef(null);

  // Keep authBotNameRef always updated
  useEffect(() => {
    authBotNameRef.current = authBotName;
  }, [authBotName]);

  // Poll bot info every 500 ms
  const fetchBotsInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      const { data } = await axios.get('/api/bots/info', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBots(data);

      setMemLimits(prev => {
        const newLimits = { ...prev };
        data.forEach(bot => {
          if (!newLimits[bot.name]) newLimits[bot.name] = '200M';
        });
        return newLimits;
      });
    } catch (err) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    fetchBotsInfo();
    intervalRef.current = setInterval(fetchBotsInfo, 500);
    return () => clearInterval(intervalRef.current);
  }, []);

  // ONE WebSocket connection for the whole component
  useEffect(() => {
    console.log('[WS] Attempting to open WebSocket...');
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[WS] No token found in localStorage!');
      return;
    }

    // Include JWT in WebSocket URL
    const wsProtocol = process.env.REACT_APP_WS_PROTOCOL;
    const wsHost = process.env.REACT_APP_WS_HOST;
    const wsPort = process.env.REACT_APP_WS_PORT;
    
    const wsUrl = `${wsProtocol}://${wsHost}:${wsPort}/ws?token=${encodeURIComponent(token)}`;
    
    console.log('[WS] Connecting to:', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    wsRef.current = ws;
    
    ws.onopen = () => console.log('[WS] Connected to backend');

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log('[WS] Received message:', msg);
    
        if (msg.type === 'qr') {
          console.log('[WS] Showing QR for:', msg.botName);
          setAuthBotName(msg.botName);
          setQrCodeData(msg.qr);
          setAuthError(null);
          setAuthModalVisible(true);
        }
		
        if (msg.type === 'qr-clear') {
          console.log('[WS] Received QR CLEAR for:', msg.botName);
          setAuthModalVisible(false);
          setQrCodeData(null);
          setAuthError(null);
        }
    
        if (msg.type === 'qr-error') {
          console.log('[WS] QR error for:', msg.botName);
          setAuthBotName(msg.botName);
          setAuthError(msg.error);
          setAuthModalVisible(true);
        }
      } catch (err) {
        console.error('Error parsing WS message:', err);
      }
    };

    ws.onerror = (err) => console.error('[WS] Error:', err);
    ws.onclose = () => console.log('[WS] Closed');

    return () => {
      console.log('[WS] Cleanup - closing');
      ws.close();
    };
  }, []); // IMPORTANT: only runs once on mount

  // Handle bot actions
  const handleAction = async (botName, action) => {
    const token = localStorage.getItem('token');

    if (action === 'authorize') {
      if (authCooldowns[botName]) {
        alert(`Авторизация для ${botName} уже запрошена. Подождите немного.`);
        return;
      }
      setAuthCooldowns(prev => ({ ...prev, [botName]: true }));
      setAuthBotName(botName);
      setQrCodeData(null);
      setAuthError(null);
      setAuthModalVisible(true);

      setTimeout(() => {
        setAuthCooldowns(prev => {
          const copy = { ...prev };
          delete copy[botName];
          return copy;
        });
      }, 60000);

      try {
        await axios.post(
          `/api/bots/${botName}/authorize`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // QR will arrive via WebSocket
      } catch (err) {
        setAuthError('Ошибка при запросе авторизации');
      }
      return;
    }

    try {
      await axios.post(
        `/api/bots/${botName}/${action}`,
        { maxMemoryRestart: memLimits[botName] || '200M' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBotsInfo();
    } catch (err) {
      console.error(`Error during ${action} for ${botName}:`, err);
      alert('Ошибка управления ботом');
    }
  };

  const handleMemLimitChange = (botName, value) => {
    setMemLimits(prev => ({ ...prev, [botName]: value }));
  };

  return (
    <>
      <AuthModal
        visible={authModalVisible}
        qrCodeData={qrCodeData}
        authError={authError}
        authBotName={authBotName}
        onClose={() => setAuthModalVisible(false)}
      />


      <table className="min-w-full border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2">Name</th>
            <th className="border p-2">Status</th>
            <th className="border p-2">Memory</th>
            <th className="border p-2">CPU</th>
            <th className="border p-2">Uptime</th>
            <th className="border p-2">Mem Limit (max-memory-restart)</th>
            <th className="border p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {bots.map(bot => (
            <tr key={bot.id}>
              <td className="border p-2">{bot.name}</td>
              <td className="border p-2">{bot.status}</td>
              <td className="border p-2">{formatBytes(bot.memory)}</td>
              <td className="border p-2">{formatCPU(bot.cpu)}</td>
              <td className="border p-2">{formatUptime(bot.uptime)}</td>
              <td className="border p-2">
                <input
                  type="text"
                  value={memLimits[bot.name] || '200M'}
                  onChange={e => handleMemLimitChange(bot.name, e.target.value)}
                  className="border rounded px-2 py-1 w-24 text-center"
                  placeholder="e.g. 200M"
                />
              </td>
              <td className="border p-2 space-x-1">
                <button
                  className="bg-green-500 text-white px-2 rounded hover:bg-green-600"
                  onClick={() => handleAction(bot.name, 'start')}
                >
                  Start
                </button>
                <button
                  className="bg-yellow-500 text-white px-2 rounded hover:bg-yellow-600"
                  onClick={() => handleAction(bot.name, 'restart')}
                >
                  Restart
                </button>
                <button
                  className="bg-red-500 text-white px-2 rounded hover:bg-red-600"
                  onClick={() => handleAction(bot.name, 'stop')}
                >
                  Stop
                </button>
                {!bot.name.startsWith('tg-') && (
                  <button
                    className="bg-blue-600 text-white px-2 rounded hover:bg-blue-700 ml-1"
                    onClick={() => handleAction(bot.name, 'authorize')}
                  >
                    (Re)Authorize
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
